import { ISettingsPointer, RoundSlider } from 'mz-react-round-slider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';

type LocalProps = {
  id: string;
  volumeOverride: number;
  onRelease?: (value: number) => void;
  volumeState: [number, Dispatch<SetStateAction<number>>];
  buttonState: [boolean, Dispatch<SetStateAction<boolean>>];
  enabledIcon: IconProp;
  disabledIcon: IconProp;
};

export const VolumeSlider = ({ id, volumeState, buttonState, enabledIcon, disabledIcon, volumeOverride, onRelease }: LocalProps) => {
  const [pointers, setPointers] = volumeState;
  const [isVolumeEnabled, setVolumeEnabled] = buttonState;
  const [trueValue, setTrueValue] = useState<ISettingsPointer[]>([{ value: pointers }]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const divContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const applyAttributes = () => {
      if (divContainerRef.current) {
        // Select all child elements within the parent div
        const allChildren = divContainerRef.current.querySelectorAll('*');

        allChildren.forEach((element) => {
          element.setAttribute('tabindex', '-1');
          element.setAttribute('focusable', 'false');
        });
      }
    };

    // Create a MutationObserver to watch for changes in the SVG content
    const observer = new MutationObserver(() => {
      applyAttributes();
    });

    // Start observing the container for changes (e.g., when the SVG is rendered)
    observer.observe(divContainerRef.current as Node, { childList: true, subtree: true });

    // Apply attributes once in case the SVG is already loaded
    applyAttributes();

    // Clean up the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleVolumeToggle = () => {
    setVolumeEnabled(!isVolumeEnabled);
  };

  useEffect(() => {
    setTrueValue([{ value: volumeOverride }]);
  }, [volumeOverride]);

  const [radius, setRadius] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateRadius = () => {
      const newRadius = Math.min(dimensions.width, dimensions.height) / 2 + 15;
      setRadius(newRadius);
    };

    updateRadius();
  }, [dimensions.height, dimensions.width]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const current = containerRef.current as HTMLDivElement;
        setDimensions({
          width: current.offsetWidth,
          height: current.offsetHeight,
        });
      }
    };

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    updateSize();
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // let debounceTimer: NodeJS.Timeout;
  // const setDebounceTimer = () => {
  //   clearTimeout(debounceTimer);
  //   debounceTimer = setTimeout(() => {
  //     if (onRelease) {
  //       onRelease(trueValue[0]?.value as number);
  //     }
  //   }, 200);
  // };

  const handleOnRelease = () => {
    if (onRelease) {
      // onRelease(trueValue[0]?.value as number);
      onRelease(pointers);
    }
  };

  return (
    <div className='overflow-hidden pb-4 pt-6' tabIndex={-1}>
      <div className='relative flex justify-center' tabIndex={-1}>
        <div
          onBlur={handleOnRelease}
          onMouseUp={handleOnRelease}
          onClick={handleOnRelease}
          onPointerLeave={handleOnRelease}
          className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform'
          ref={divContainerRef}
          tabIndex={-1}
        >
          <RoundSlider
            hideText
            pointers={trueValue}
            onChange={(value) => {
              setPointers(parseInt(value[0]?.value?.toString() ?? '') ?? 0);
              setTrueValue(value);
            }}
            pathStartAngle={140}
            pathEndAngle={40}
            pathRadius={radius}
            pathThickness={10}
            pathBgColor='#121824'
            pathBorder={1}
            pathBorderColor='#2D618C'
            pointerBgColor='#FFFFFF'
            pointerBgColorSelected='#FFFFFF'
            pointerBgColorHover='#FFFFFF'
            pointerBorder={2}
            pointerBorderColor='#34d399'
            pointerRadius={8}
            SvgDefs={
              <linearGradient id='connection' x1='100%' y1='0%' x2='0%' y2='0%'>
                <stop offset='0%' stopColor='#0d9488' />
                <stop offset='100%' stopColor='#115e59' />
              </linearGradient>
            }
            connectionBgColor={'url(#connection)'}
          />
        </div>
        <button
          id={id}
          ref={containerRef}
          className='theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] bg-primary relative aspect-square w-4/6 rounded-full sm:w-2/3 md:w-2/3 lg:w-4/6'
          onClick={handleVolumeToggle}
          tabIndex={-1}
        >
          <FontAwesomeIcon
            icon={isVolumeEnabled ? enabledIcon : disabledIcon}
            className={clsx(isVolumeEnabled ? 'h-12 w-12 text-emerald-400' : 'h-14 w-14 text-orange-500', 'p-2')}
          />
        </button>
      </div>
    </div>
  );
};
